import * as Sentry from '@sentry/browser';
import { sentryExceptionHandlerFactory } from './sentry/sentryExceptionHandler';
import { sentryHttpInterceptorFactory } from './sentry/sentryHttpInterceptor';
import { resolveSentryProduct } from './sentry/resolveSentryProduct';

// Sentry initializes as a side effect here so all consuming products get it automatically
// when csmCommonWeb.bundle.js loads via the master page. SENTRY_DSN is '' in dev builds,
// making this a no-op locally.
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    // release must match the SENTRY_RELEASE the source maps upload under, or stack traces won't resolve.
    release: SENTRY_RELEASE,
    environment: APP_ENVIRONMENT
    // NOTE: performance monitoring is intentionally NOT enabled. Turning it on requires
    // registering the BrowserTracing integration here as well as setting tracesSampleRate --
    // a rate on its own produces no transactions. Add both together, and start well below
    // 1.0: this bundle runs on every page of ~10 products, so the transaction volume (and
    // Sentry quota cost) is roughly the sampled share of all page loads across the suite.
  });
  // All products share one Sentry project; this tag is derived from the URL so each product's
  // events stay filterable. See resolveSentryProduct for the host/path mapping.
  Sentry.setTag('product', resolveSentryProduct());
}

//jQuery, $ will be all exposed via a webpack plugin. See webpack.config file
// import $ from 'jquery';
// import "expose-loader?exposes=$,jQuery!jquery/jquery.js";

import 'drag-drop-touch';

// bootstrap is a dependency of angular-ui-bootstrap and needs to be loaded here to work.
import 'bootstrap'; // This brings in all of the bootstrap javascript
// The bootstrap css will need to be brought in by the consuming
// application, however it is listed here for reference.
import 'bootstrap/dist/css/bootstrap.css';

import angular from 'angular';

import changeLoginDialog from './com/ui/ChangeLoginDialog/ChangeLoginDialog';
import ng from './ng/ng';
import pace from './payments/pace/pace';
import polyfill from './polyfill/polyfill';
import remoting from './remoting/remoting';
import styles from './styles/styles';
import text from './text/text';

/** CSM UI */
import csmUI from './ui/ui';
import addressInput from './ui/addressInput/addressInput';
import alert from './ui/alert/alert';
import busyScreen from './ui/busyScreen/busyScreen';
import charCounter from './ui/charCounter/charCounter';
import checkBoxGroup from './ui/checkboxGroup/checkboxGroup';
import commonMenu from './ui/commonMenu/commonMenu';
import csmColorPicker from './ui/csmColorPicker/csmColorPicker';
import csmFocus from './ui/csmFocus/csmFocus';
import dateInput from './ui/dateInput/dateInput';
import dateRangeSelector from './ui/dateRangeSelector/dateRangeSelector';
import dateTimePicker from './ui/dateTimePicker/dateTimePicker';
import events from './ui/events/events';
import fields from './ui/fields/fields';
import gridPagination from './ui/gridPagination/gridPagination';
import imageLoaderPanel from './ui/ImageLoaderPanel/ImageLoaderPanel';
import importForm from './ui/importForm/importForm';
import initFocus from './ui/initFocus/initFocus';
import inlineInput from './ui/inlineInput/inlineInput';
import inlineLabelGroup from './ui/inlineLabelGroup/inlineLabelGroup';
import lazyTypeahead from './ui/lazyTypeahead/lazyTypeahead';
import maxCounter from './ui/maxCounter/maxCounter';
import moreInfoDialog from './ui/moreInfoDialog/moreInfoDialog';
import multiSelect from './ui/multiSelect/multiSelect';
import orgSelectDialog from './ui/orgSelectDialog/orgSelectDialog';
import phoneInput from './ui/phoneInput/phoneInput';
import reportFormat from './ui/reports/reportFormat/reportFormat';
import reportTemplate from './ui/reports/reportTemplate/reportTemplate';
import shuttleList from './ui/shuttleList/shuttleList';
import stateSelector from './ui/stateSelector/stateSelector';
import userImage from './ui/userImage/userImage';
import validators from './ui/validators/validators';
import valueDisplay from './ui/valueDisplay/valueDisplay';
import receiptCard from './ui/receiptCard/receiptCard';
import missingReceiptsAdmin from './ui/MissingReceiptsAdmin/MissingReceiptsAdmin';

/**
 * Sentry test UI, compiled in only when SENTRY_TEST_UI is set at build time.
 *
 * SENTRY_TEST_UI is a DefinePlugin boolean, so webpack folds the ternary and drops the
 * require() entirely from a normal Release build -- the component is absent from the
 * installer rather than merely hidden. `utils` sets it for Sentry smoke-test builds only.
 *
 * A static import would defeat this: `angular.module()` runs as an import side effect, so
 * the module would register regardless of the flag. The require has to stay inside the
 * branch for the elimination to work.
 */
const sentryTestButtonModule = SENTRY_TEST_UI
  ? require('./ui/sentryTestButton/sentryTestButton')
  : null;
const sentryTestModules = sentryTestButtonModule
  ? [sentryTestButtonModule.default || sentryTestButtonModule]
  : [];

/** csm.util */
import csmHTTP from './util/CSMHttp/CSMHttp';
import csmTools from './util/tools/tools';
import commonSVC from './util/CommonSvc/CommonSvc';
import constantTable from './util/ConstantTable/ConstantTable';
import filters from './util/filters/filters';
import flash from './util/Flash/Flash';
import keyCodes from './util/keycodes/keycodes';
import localDateSerializer from './util/LocalDateSerializer/LocalDateSerializer';
import rowUpdateManager from './util/RowUpdateManager/RowUpdateManager';
import shortcuts from './util/shortcuts/shortcuts';
import spaceClick from './util/spaceClick/spaceClick';

/** vendors */
import vendors from './vendors/vendors';
import uiGrid from './vendors/ui-grid/ui-grid';

export {
  PosPrintReceipt,
  TEST_RECEIPT
} from './util/posPrintService/PosPrintReceipt';
export {
  getReceiptDues,
  getAccountName
} from './util/receiptFormatting/receiptFormatting';

export default angular
  .module('CSMCommonWeb', [
    changeLoginDialog,
    ng,
    pace,
    polyfill,
    remoting,
    styles,
    text,
    csmUI,
    addressInput,
    alert,
    busyScreen,
    charCounter,
    checkBoxGroup,
    commonMenu,
    csmColorPicker,
    csmFocus,
    dateInput,
    dateRangeSelector,
    dateTimePicker,
    events,
    fields,
    gridPagination,
    imageLoaderPanel,
    importForm,
    initFocus,
    inlineInput,
    inlineLabelGroup,
    lazyTypeahead,
    maxCounter,
    moreInfoDialog,
    multiSelect,
    orgSelectDialog,
    phoneInput,
    reportFormat,
    reportTemplate,
    shuttleList,
    stateSelector,
    userImage,
    validators,
    valueDisplay,
    receiptCard,
    /** Empty unless this is a SENTRY_TEST_UI build. See sentryTestModules above. */
    ...sentryTestModules,
    /** Utils */
    csmHTTP,
    constantTable,
    filters,
    flash,
    keyCodes,
    localDateSerializer,
    rowUpdateManager,
    missingReceiptsAdmin,
    shortcuts,
    spaceClick,
    csmTools,
    commonSVC,
    /** Vendors */
    vendors,
    uiGrid
  ])
  // Registered here so all products inherit a single Sentry-aware handler.
  // AngularJS's "last registration wins" rule means this replaces the per-app
  // duplicates that are removed as part of the Sentry migration.
  .factory('$exceptionHandler', sentryExceptionHandlerFactory)
  .factory('sentryHttpInterceptor', sentryHttpInterceptorFactory)
  .config([
    '$httpProvider',
    function ($httpProvider) {
      // $exceptionHandler does not see HTTP responses � only uncaught JS exceptions.
      // This interceptor fills that gap by capturing 5xx API errors for all products.
      $httpProvider.interceptors.push('sentryHttpInterceptor');
    }
  ]).name;
